import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ComparePanel } from '@/components/ComparePanel';
import { DrinkCombobox } from '@/components/DrinkCombobox';
import { PriceEditor } from '@/components/PriceEditor';
import { formatDrinkDisplayName } from '@/lib/displayName';
import { FEATURED_COMPARES, getFeaturedCompare } from '@/lib/featuredCompares';
import { encodePriceFragment, encodeShareQuery } from '@/lib/shareState';
import { useCatalog } from '@/hooks/CatalogContext';
import { track } from '@/lib/analytics';

export function ComparePage() {
  const { pair } = useParams();
  const [params, setParams] = useSearchParams();
  const {
    rows,
    loading,
    overrides,
    prefs,
    filters,
    baselineId,
    saveOverride,
    clearOverride,
  } = useCatalog();
  const [includePrices, setIncludePrices] = useState(false);
  const [shareMsg, setShareMsg] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const ids = useMemo(() => {
    const fromQuery = (params.get('c') ?? '')
      .split(',')
      .filter(Boolean)
      .slice(0, 4);
    if (fromQuery.length) return fromQuery;
    if (pair) {
      const [aSlug, bSlug] = pair.split('-vs-');
      const a = rows.find((r) => r.product.slug === aSlug);
      const b = rows.find((r) => r.product.slug === bSlug);
      return [a?.product.id, b?.product.id].filter(Boolean) as string[];
    }
    return [];
  }, [params, pair, rows]);

  const selected = ids
    .map((id) => rows.find((r) => r.product.id === id))
    .filter(Boolean) as typeof rows;

  const drinksForSelect = useMemo(
    () =>
      [...rows].sort((a, b) =>
        formatDrinkDisplayName(a.product.name).localeCompare(
          formatDrinkDisplayName(b.product.name),
          undefined,
          { sensitivity: 'base' },
        ),
      ),
    [rows],
  );

  const editingRow =
    editingId != null
      ? selected.find((r) => r.product.id === editingId) ?? null
      : null;

  function toggle(id: string) {
    const next = ids.includes(id)
      ? ids.filter((x) => x !== id)
      : [...ids, id].slice(0, 4);
    setParams(next.length ? { c: next.join(',') } : {});
    track('comparison_opened', { count: next.length });
  }

  async function share() {
    const q = encodeShareQuery({
      compareIds: ids,
      baselineId,
      category: filters.category,
      subtype: filters.subtype,
      sortKey: prefs.sortKey,
      sortDirection: prefs.sortDirection,
      query: filters.query,
    });
    let url = `${window.location.origin}/compare/${q ? `?${q}` : ''}`;
    if (includePrices && Object.keys(overrides).length) {
      url += `#${encodePriceFragment(overrides)}`;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareMsg(
        includePrices
          ? 'Link copied — includes your prices in the URL fragment (labeled user-entered).'
          : 'Link copied — reference data only.',
      );
      track('share_clicked', { includePrices });
    } catch {
      setShareMsg(url);
    }
  }

  if (loading) return <p>Loading…</p>;

  const featured = pair ? getFeaturedCompare(pair) : undefined;

  return (
    <>
      <section className="hero">
        <p className="eyebrow">Compare</p>
        <h1>{featured?.title ?? 'Side-by-side drinks'}</h1>
        {featured ? (
          <p>{featured.summary}</p>
        ) : (
          <p>
            Pick up to four. Change servings on any drink to match the same
            alcohol across the group and see total calories. First selected drink
            also anchors percentage wording. Edit package prices to recalculate
            value. See also <Link to="/guides/">guides</Link>.
          </p>
        )}
      </section>

      {featured && (
        <article
          className="panel prose-page"
          dangerouslySetInnerHTML={{ __html: featured.bodyHtml }}
        />
      )}

      {!pair && (
        <section className="panel prose-page">
          <h2>Featured written comparisons</h2>
          <ul>
            {FEATURED_COMPARES.map((c) => (
              <li key={c.slug}>
                <Link to={`/compare/${c.slug}/`}>{c.title}</Link>
                <span className="muted"> — {c.summary}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="panel">
        <DrinkCombobox
          drinks={drinksForSelect}
          selectedIds={ids}
          disabled={ids.length >= 4}
          onPick={toggle}
        />
        <div className="chip-row" style={{ marginTop: '0.75rem' }}>
          {ids.map((id) => {
            const row = rows.find((r) => r.product.id === id);
            if (!row) return null;
            return (
              <button
                key={id}
                type="button"
                className="chip"
                aria-pressed="true"
                onClick={() => toggle(id)}
              >
                {formatDrinkDisplayName(row.product.name)} ×
              </button>
            );
          })}
        </div>
        <label className="check-row">
          <input
            type="checkbox"
            checked={includePrices}
            onChange={(e) => setIncludePrices(e.target.checked)}
          />
          Include my prices in share link (URL fragment only)
        </label>
        <div className="row-actions">
          <button type="button" className="btn btn-primary" onClick={share}>
            Copy share link
          </button>
          <Link className="btn" to="/">
            Catalog
          </Link>
        </div>
        {shareMsg && <p className="muted">{shareMsg}</p>}
      </div>

      <ComparePanel
        rows={selected}
        onEditPrice={(id) => setEditingId(id)}
      />

      {editingRow && (
        <div id="price-editor" tabIndex={-1}>
          <PriceEditor
            row={editingRow}
            existing={overrides[editingRow.product.id]}
            onSave={(o) => {
              saveOverride(o);
              setEditingId(null);
            }}
            onReset={() => clearOverride(editingRow.product.id)}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setEditingId(null)}
          >
            Close editor
          </button>
        </div>
      )}
    </>
  );
}
